import type { ClientRect } from "@dnd-kit/core";
import { SCROLLER_ATTRIBUTE } from "./boardAttributes";
import {
    getLandingDurationMs,
    type BoardDropLanding,
    type BoardMotion,
} from "./boardMotion";

export interface VisibleArea {
    top: number;
    bottom: number;
}

const toPixels = (value: string) => {
    const pixels = Number.parseFloat(value);
    return Number.isFinite(pixels) ? pixels : 0;
};

/**
 * The part of a scroller cards show in: inside its borders and horizontal
 * scrollbar, clear of its sticky rows and the command island (its scroll padding).
 */
export const getVisibleArea = (scroller: HTMLElement): VisibleArea => {
    const { scrollPaddingTop, scrollPaddingBottom } =
        getComputedStyle(scroller);
    const top = scroller.getBoundingClientRect().top + scroller.clientTop;
    return {
        top: top + toPixels(scrollPaddingTop),
        bottom: top + scroller.clientHeight - toPixels(scrollPaddingBottom),
    };
};

const getScroller = (node: HTMLElement) => {
    const scroller = node.closest(`[${SCROLLER_ATTRIBUTE}]`);
    return scroller instanceof HTMLElement ? scroller : null;
};

/** How far the scroller has to move to show the whole card, nearest edge first. */
const getOffset = (
    rect: Pick<ClientRect, "top" | "bottom">,
    area: VisibleArea,
) => {
    if (rect.top < area.top) {
        return rect.top - area.top;
    }
    return rect.bottom > area.bottom
        ? Math.min(rect.bottom - area.bottom, rect.top - area.top)
        : 0;
};

/**
 * Where a dropped card will show once its column has scrolled it into view.
 * dnd-kit measures the card through this before the drop animation, and
 * scrolls it to the window's centre when the rect is off-screen
 * (`scrollIntoViewIfNeeded` in DragOverlay's `useDropAnimation`). That was
 * the jump; a rect already in view leaves the scrolling to the landing.
 */
export const getLandingRect = (node: HTMLElement, rect: ClientRect) => {
    const scroller = getScroller(node);
    if (scroller === null) {
        return rect;
    }
    const offset = getOffset(rect, getVisibleArea(scroller));
    return {
        ...rect,
        top: rect.top - offset,
        bottom: rect.bottom - offset,
    };
};

// cubic-bezier(0.23, 1, 0.32, 1), the `--ease-out` token, is easeOutQuint.
const easeOut = (progress: number) => 1 - (1 - progress) ** 5;

/** How far to jump to one view away from the card; 0 when it is closer. */
const getJumpWithinView = (offset: number, area: VisibleArea) => {
    const viewHeight = area.bottom - area.top;
    return Math.abs(offset) > viewHeight
        ? offset - Math.sign(offset) * viewHeight
        : 0;
};

const jumpWithinView = ({
    scroller,
    offset,
    area,
}: {
    scroller: HTMLElement;
    offset: number;
    area: VisibleArea;
}) => {
    const jump = getJumpWithinView(offset, area);
    if (jump !== 0) {
        scroller.scrollTop += jump;
    }
};

const USER_SCROLL_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"];

/**
 * Glides the scroller until the card is in view. Each frame covers its eased
 * share of the distance left, measured afresh, so the glide still ends on the
 * card when a windowed list re-measures the cards above it mid-way (and
 * jumps again if that leaves the card more than a view away). The glide
 * stops as soon as the user scrolls the column themselves.
 */
const glideTo = ({
    node,
    scroller,
    durationMs,
}: {
    node: HTMLElement;
    scroller: HTMLElement;
    durationMs: number;
}) =>
    new Promise<void>((resolve) => {
        let start: number | undefined;
        let covered = 0;
        let isInterrupted = false;
        const interrupt = () => {
            isInterrupted = true;
        };
        for (const type of USER_SCROLL_EVENTS) {
            scroller.addEventListener(type, interrupt, { passive: true });
        }
        const finish = () => {
            for (const type of USER_SCROLL_EVENTS) {
                scroller.removeEventListener(type, interrupt);
            }
            resolve();
        };
        const step = (now: number) => {
            if (isInterrupted || !node.isConnected) {
                finish();
                return;
            }
            start ??= now;
            const area = getVisibleArea(scroller);
            jumpWithinView({
                scroller,
                offset: getOffset(node.getBoundingClientRect(), area),
                area,
            });
            const progress = Math.min(1, (now - start) / durationMs);
            const eased = easeOut(progress);
            const share =
                progress === 1 ? 1 : (eased - covered) / (1 - covered);
            scroller.scrollTop +=
                getOffset(node.getBoundingClientRect(), area) * share;
            covered = eased;
            if (progress < 1) {
                requestAnimationFrame(step);
            } else {
                finish();
            }
        };
        requestAnimationFrame(step);
    });

export interface BoardLanding extends BoardDropLanding {
    scroller: HTMLElement;
}

/**
 * How a card at `rect` lands in `area`: the scroller's first `jump`, then
 * the `glide` left. A card further than one view away jumps to one view
 * away first, so the glide is always short; reduced motion jumps the whole
 * way. Null when the card is already in view.
 */
export const getLandingPlan = ({
    rect,
    area,
    motion,
}: {
    rect: Pick<ClientRect, "top" | "bottom">;
    area: VisibleArea;
    motion: Pick<BoardMotion, "isReduced">;
}) => {
    const offset = getOffset(rect, area);
    if (offset === 0) {
        return null;
    }
    const jump = motion.isReduced ? offset : getJumpWithinView(offset, area);
    return {
        jump,
        glide: offset - jump,
        wasOutOfView: rect.bottom <= area.top || rect.top >= area.bottom,
    };
};

/**
 * Scrolls a dropped card's own scroller (only that one, only vertically)
 * until the card is in view, as `getLandingPlan` lays out.
 */
export const startLanding = (
    node: HTMLElement,
    motion: BoardMotion,
): BoardLanding | null => {
    const scroller = getScroller(node);
    if (scroller === null) {
        return null;
    }
    const plan = getLandingPlan({
        rect: node.getBoundingClientRect(),
        area: getVisibleArea(scroller),
        motion,
    });
    if (plan === null) {
        return null;
    }
    if (plan.jump !== 0) {
        scroller.scrollTop += plan.jump;
    }
    return {
        scroller,
        wasOutOfView: plan.wasOutOfView,
        finished:
            plan.glide === 0
                ? Promise.resolve()
                : glideTo({
                      node,
                      scroller,
                      durationMs: getLandingDurationMs(motion),
                  }),
    };
};
